// favicon.ts — loads the first usable image from a list, and sets the page's favicon from it. The favicon is drawn
// whole and centered on a transparent 32x32 canvas, never stretched: a square icon fills it, a wide logo is scaled
// down to fit. The caller states the order (see iconSources in orgIcon.js); a source that is missing or is not an
// image (a 404) is skipped.
const SIZE = 32;

/** The first of `sources` that loads as an image, or null. */
export function loadFirstImage(sources: Array<string | undefined | null>): Promise<HTMLImageElement | null> {
  const queue = sources.filter((s): s is string => !!s);
  return new Promise((resolve) => {
    const tryNext = () => {
      const src = queue.shift();
      if (!src) return resolve(null);
      // Do NOT set crossOrigin on same-origin assets: it forces CORS mode, which taints the canvas
      // when ACAO headers are absent, causing toDataURL() to throw.
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = tryNext;
      img.src = src;
    };
    tryNext();
  });
}

export async function setFavicon(sources: Array<string | undefined | null>): Promise<void> {
  const img = await loadFirstImage(sources);
  if (!img) return;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const scale = img.width && img.height ? Math.min(SIZE / img.width, SIZE / img.height) : 1;
  const w = img.width ? img.width * scale : SIZE;
  const h = img.height ? img.height * scale : SIZE;
  ctx.drawImage(img, (SIZE - w) / 2, (SIZE - h) / 2, w, h);
  try {
    let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.setAttribute('rel', 'icon');
      document.head.appendChild(favicon);
    }
    favicon.setAttribute('href', canvas.toDataURL('image/png'));
    console.log(`[Favicon] set from: ${img.src}`);
  } catch (e) {
    console.log('[Favicon] canvas toDataURL failed (tainted?):', JSON.stringify(e));
  }
}
